import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Lock, Unlock, Upload, Loader2, DollarSign, Users, Baby, Receipt, ChevronDown, ChevronRight, KeyRound, ShieldCheck, LogOut as LockIcon, Trash2,
  PieChart, Plus, TrendingDown, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import MySubscriptionPanel from "@/components/dashboard/MySubscriptionPanel";
import ParentInvoicesPanel from "@/components/dashboard/ParentInvoicesPanel";

const TOKEN_KEY = "cc_billing_token";

// iter-81ap — percentage ladder used by the per-family % selector AND by the
// spending tracker so owners can quickly see how much each expense costs
// relative to their total income.
const PERCENT_LADDER = [100, 90, 80, 70, 60, 50, 45, 40, 35, 30, 25, 20, 15, 10, 5];

const EXPENSE_CATEGORIES = [
  { value: "rent",                 label: "Rent / Facility" },
  { value: "utilities",            label: "Utilities" },
  { value: "food",                 label: "Food & Meals" },
  { value: "supplies",             label: "Supplies" },
  { value: "education_resources",  label: "Education Resources" },
  { value: "staff_wages",          label: "Staff Wages" },
  { value: "insurance",            label: "Insurance" },
  { value: "maintenance",          label: "Maintenance" },
  { value: "marketing",            label: "Marketing" },
  { value: "other",                label: "Other" },
];
const CATEGORY_LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map(c => [c.value, c.label]));

export default function Billing() {
  const [status, setStatus] = useState("loading"); // loading | setup | locked | unlocked
  const [passcode, setPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [summary, setSummary] = useState(null);
  const [families, setFamilies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [period, setPeriod] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [spendingSummary, setSpendingSummary] = useState(null);
  const [familyPct, setFamilyPct] = useState({}); // { "case::name": 50, ... }
  const [historyMeta, setHistoryMeta] = useState(null); // months/periods rollup
  const [rangeMode, setRangeMode] = useState("all"); // all | this_month | last_month | last_3_months | this_year | month | week
  const [rangeMonth, setRangeMonth] = useState(""); // "YYYY-MM" when rangeMode === "month"
  const fileRef = useRef(null);

  // Check lock status on mount
  useEffect(() => {
    api.get("/billing/passcode/status")
      .then((r) => {
        if (!r.data.is_set) setStatus("setup");
        else if (token) { loadAll(token); }
        else setStatus("locked");
      })
      .catch((err) => {
        toast.error(formatApiError(err));
        setStatus("locked");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withToken = (t = token) => ({ headers: { "X-Billing-Token": t } });

  // iter-81aq — compute the {gte, lte} date range from the selected range mode.
  const rangeParams = useMemo(() => {
    const today = new Date();
    const isoDate = (d) => d.toISOString().slice(0, 10);
    if (rangeMode === "all") return {};
    if (rangeMode === "this_month") {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { period_start_gte: isoDate(s), period_start_lte: isoDate(e) };
    }
    if (rangeMode === "last_month") {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { period_start_gte: isoDate(s), period_start_lte: isoDate(e) };
    }
    if (rangeMode === "last_3_months") {
      const s = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      return { period_start_gte: isoDate(s) };
    }
    if (rangeMode === "this_year") {
      const s = new Date(today.getFullYear(), 0, 1);
      return { period_start_gte: isoDate(s) };
    }
    if (rangeMode === "month" && rangeMonth) {
      const [y, m] = rangeMonth.split("-").map(Number);
      const s = new Date(y, m - 1, 1);
      const e = new Date(y, m, 0);
      return { period_start_gte: isoDate(s), period_start_lte: isoDate(e) };
    }
    if (rangeMode === "week" && period && period !== "all") {
      return { period_start: period };
    }
    return {};
  }, [rangeMode, rangeMonth, period]);

  const loadAll = async (t = token, rp = rangeParams) => {
    try {
      const authed = { ...withToken(t), params: rp };
      const authedNoRange = withToken(t);
      const [s, f, p, sp, fp, hm] = await Promise.all([
        api.get("/billing/summary", authed),
        api.get("/billing/families", authed),
        api.get("/billing/payments", authed),
        api.get("/billing/spending-summary", authed),
        api.get("/billing/families/percentages", authedNoRange),
        api.get("/billing/history-meta", authedNoRange),
      ]);
      setSummary(s.data); setFamilies(f.data); setPayments(p.data);
      setSpendingSummary(sp.data);
      setFamilyPct(fp.data || {});
      setHistoryMeta(hm.data || null);
      setStatus("unlocked");
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(""); setStatus("locked");
        toast.error("Billing session expired — enter your passcode.");
      } else toast.error(formatApiError(err));
    }
  };

  // iter-81aq — reload data when the range changes (skip while locked).
  useEffect(() => {
    if (status !== "unlocked" || !token) return;
    loadAll(token, rangeParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeParams, status]);

  // iter-81ap — persist per-family percentage selection.
  const setFamilyPercentage = async (familyKey, percentage) => {
    setFamilyPct((s) => ({ ...s, [familyKey]: percentage }));
    try {
      await api.put(
        "/billing/families/percentages",
        { family_key: familyKey, percentage },
        withToken(),
      );
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Add / delete expenses — refresh the spending summary after.
  const reloadSpending = async () => {
    try {
      const sp = await api.get("/billing/spending-summary", { ...withToken(), params: rangeParams });
      setSpendingSummary(sp.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const addExpense = async (payload) => {
    try {
      await api.post("/billing/expenses", payload, withToken());
      await reloadSpending();
      toast.success("Expense added");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deleteExpense = async (id) => {
    try {
      await api.delete(`/billing/expenses/${id}`, withToken());
      await reloadSpending();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setupPasscode = async (e) => {
    e.preventDefault();
    if (passcode.length < 4) return toast.error("Passcode must be at least 4 characters.");
    if (passcode !== confirmPasscode) return toast.error("Passcodes don't match.");
    setBusy(true);
    try {
      const r = await api.post("/billing/passcode/set", { passcode });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      toast.success("Billing passcode set — billing is now locked to this passcode.");
      setPasscode(""); setConfirmPasscode("");
      await loadAll(r.data.token);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/billing/passcode/verify", { passcode });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      toast.success("Billing unlocked — session expires in 30 minutes.");
      setPasscode("");
      await loadAll(r.data.token);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const lockNow = async () => {
    try {
      await api.post("/billing/passcode/lock");
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(""); setStatus("locked"); setSummary(null); setFamilies([]); setPayments([]);
      toast.success("Billing locked.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/billing/import", fd, {
        headers: { "Content-Type": "multipart/form-data", "X-Billing-Token": token },
      });
      toast.success(`Imported ${r.data.inserted} · updated ${r.data.updated}${r.data.skipped ? ` · skipped ${r.data.skipped}` : ""}`);
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  /** Delete an uploaded billing week / month / everything.
   * iter-81c8 — extended from single-week delete to also support:
   *  - deletePeriod("month") when the user has picked a month filter
   *  - deletePeriod("all") for a full-history wipe (extra typed confirmation)
   * Kept the original "week" path so the per-week button still works. */
  const deletePeriod = async (mode = "week") => {
    let qs = "";
    let confirmMsg = "";
    if (mode === "week") {
      if (rangeMode !== "week" || !period || period === "all") return;
      qs = `period_start=${encodeURIComponent(period)}`;
      confirmMsg = `Delete the week of ${period}? This permanently removes the imported rows for that week. You can re-upload the CSV any time.`;
    } else if (mode === "month") {
      if (rangeMode !== "month" || !rangeMonth) return;
      qs = `month=${encodeURIComponent(rangeMonth)}`;
      confirmMsg = `Delete every uploaded week in ${monthLabel(rangeMonth)}? This permanently removes all payment rows for that month. You can re-upload the CSVs any time.`;
    } else if (mode === "all") {
      qs = `all=true`;
      const typed = window.prompt(
        "This will permanently delete ALL uploaded billing history (every week, every month). Type DELETE ALL to confirm."
      );
      if ((typed || "").trim().toUpperCase() !== "DELETE ALL") {
        toast.info("Cancelled — nothing was deleted.");
        return;
      }
      confirmMsg = "";  // already confirmed via typed prompt
    } else {
      return;
    }
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/billing/payments?${qs}`,
        { headers: { "X-Billing-Token": token } });
      const total = r?.data?.deleted ?? 0;
      const label = mode === "week"  ? `week ${period}`
                  : mode === "month" ? monthLabel(rangeMonth)
                  : "billing history";
      toast.success(`Removed ${total} row${total === 1 ? "" : "s"} for ${label}.`);
      setRangeMode("all");
      setPeriod("all");
      setRangeMonth("");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const filteredFamilies = families;

  // ----- Render gates -----
  if (status === "loading") {
    return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;
  }

  if (status === "setup") {
    return (
      <LockScreen title="Set a billing passcode" icon={KeyRound}
        description="Billing is owner-only. Choose a passcode — you'll need it every time you open this page. 30-minute sessions.">
        <form onSubmit={setupPasscode} className="space-y-3" data-testid="billing-setup-form">
          <PasscodeInput label="New passcode" value={passcode} onChange={setPasscode} testId="billing-set-passcode" />
          <PasscodeInput label="Confirm passcode" value={confirmPasscode} onChange={setConfirmPasscode} testId="billing-confirm-passcode" />
          <Button type="submit" disabled={busy} data-testid="billing-set-submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Set passcode & unlock</>}
          </Button>
        </form>
      </LockScreen>
    );
  }

  if (status === "locked") {
    return (
      <LockScreen title="Billing is locked" icon={Lock}
        description="Enter the owner passcode to unlock billing. If you're not the owner, contact your center owner for access.">
        <form onSubmit={unlock} className="space-y-3" data-testid="billing-unlock-form">
          <PasscodeInput label="Owner passcode" value={passcode} onChange={setPasscode} testId="billing-unlock-passcode" autoFocus />
          <Button type="submit" disabled={busy} data-testid="billing-unlock-submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : <><Unlock className="w-4 h-4 mr-2" /> Unlock billing</>}
          </Button>
        </form>
      </LockScreen>
    );
  }

  // ----- Unlocked dashboard -----
  return (
    <div className="space-y-8" data-testid="billing-unlocked">
      <MySubscriptionPanel />

      <ParentInvoicesPanel />

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">DCY payment roster</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Billing — families & expected amounts</h1>
          <p className="mt-2 text-stone-500 max-w-2xl">Import your DCY payment export CSV. ChildCheck groups by <b>Case Number</b> and shows the expected amount per family.</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => uploadCsv(e.target.files?.[0])} data-testid="billing-file-input" />
          <Button data-testid="billing-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Import CSV</>}
          </Button>
          <Button data-testid="billing-lock-btn" variant="outline" onClick={lockNow} className="rounded-xl h-11"><LockIcon className="w-4 h-4 mr-1" /> Lock</Button>
        </div>
      </header>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testId="bill-stat-expected" label="Expected this period" value={summary ? `$${summary.total_amount.toLocaleString()}` : "—"} Icon={DollarSign} tone="emerald" />
        <StatCard testId="bill-stat-families" label="Families" value={summary?.families ?? "—"} Icon={Users} />
        <StatCard testId="bill-stat-children" label="Children" value={summary?.children ?? "—"} Icon={Baby} tone="orange" />
        <StatCard testId="bill-stat-payments" label="Payments" value={summary?.payments ?? "—"} Icon={Receipt} />
        <StatCard testId="bill-stat-waiting" label="Waiting reconciliation" value={summary?.waiting_reconciliation ?? "—"} Icon={Lock} tone="amber" />
      </div>

      {/* History banner + quick filters (iter-81aq) */}
      <BillingHistoryFilters
        historyMeta={historyMeta}
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        rangeMonth={rangeMonth}
        setRangeMonth={setRangeMonth}
        period={period}
        setPeriod={setPeriod}
        deletePeriod={deletePeriod}
        busy={busy}
      />

      {/* Spending Tracker (iter-81ap) */}
      <SpendingTracker
        spending={spendingSummary}
        onAdd={addExpense}
        onDelete={deleteExpense}
      />

      {/* Families table */}
      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-stone-200 flex items-center justify-between">
          <div>
            <div className="font-display text-xl text-stone-900">Families</div>
            <div className="text-xs text-stone-500">{filteredFamilies.length} grouped by case number</div>
          </div>
        </div>
        {filteredFamilies.length === 0 ? (
          <div className="p-14 text-center">
            <Receipt className="w-8 h-8 mx-auto text-stone-300" />
            <p className="mt-3 font-display text-xl text-stone-900">No billing data yet.</p>
            <p className="mt-1 text-sm text-stone-500">Click <b>Import CSV</b> and upload your DCY payment export.</p>
          </div>
        ) : (
          <table className="w-full" data-testid="billing-families-table">
            <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest text-stone-500">
              <tr>
                <th className="text-left font-semibold px-4 py-3 w-8"></th>
                <th className="text-left font-semibold px-4 py-3">Family</th>
                <th className="text-left font-semibold px-4 py-3">Case #</th>
                <th className="text-right font-semibold px-4 py-3"><Baby className="w-3.5 h-3.5 inline" /> Children</th>
                <th className="text-right font-semibold px-4 py-3">Co-pay</th>
                <th className="text-right font-semibold px-4 py-3"><DollarSign className="w-3.5 h-3.5 inline" /> Expected</th>
                <th className="text-right font-semibold px-4 py-3">% of family</th>
              </tr>
            </thead>
            <tbody>
              {filteredFamilies.map((fam) => {
                const key = `${fam.case_number}::${fam.family_name}`;
                const isOpen = !!expanded[key];
                const familyTotal = (fam.expected_amount || 0) + (fam.copay_total || 0);
                const pctSelected = familyPct[key];
                const pctAmount = pctSelected ? (familyTotal * pctSelected) / 100 : null;
                const rows = [
                  <tr key={`${key}-main`} data-testid={`billing-family-row-${fam.case_number}`}
                      className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3 text-stone-400 cursor-pointer"
                        onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}>
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="px-4 py-3 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}>
                      <div className="font-medium text-stone-900">{fam.family_name || "—"}</div>
                      <div className="text-[11px] text-stone-500">Period {fam.period_start || "—"}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-stone-700 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}>{fam.case_number}</td>
                    <td className="px-4 py-3 text-right text-stone-700 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}>{fam.child_count}</td>
                    <td className="px-4 py-3 text-right text-stone-700 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}>${fam.copay_total.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-900 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}>${fam.expected_amount.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <select
                          value={pctSelected || ""}
                          onChange={(e) => setFamilyPercentage(key, parseInt(e.target.value, 10))}
                          onClick={(e) => e.stopPropagation()}
                          className="h-8 rounded-md border border-stone-200 bg-white px-1.5 text-xs font-semibold text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          data-testid={`family-pct-select-${fam.case_number}`}
                        >
                          <option value="">— %</option>
                          {PERCENT_LADDER.map(p => (
                            <option key={p} value={p}>{p}%</option>
                          ))}
                        </select>
                        {pctAmount != null && (
                          <span
                            className="text-[11px] font-mono text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
                            data-testid={`family-pct-amount-${fam.case_number}`}
                          >
                            ${pctAmount.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>,
                ];
                if (isOpen) {
                  rows.push(
                    <tr key={`${key}-detail`} className="bg-stone-50/60 border-b border-stone-100" data-testid={`billing-family-detail-${fam.case_number}`}>
                      <td colSpan={7} className="px-6 py-4">
                        <table className="w-full text-sm">
                          <thead className="text-[10px] uppercase tracking-widest text-stone-500">
                            <tr>
                              <th className="text-left font-semibold pb-2">Child</th>
                              <th className="text-left font-semibold pb-2">Payment ID</th>
                              <th className="text-left font-semibold pb-2">Status</th>
                              <th className="text-left font-semibold pb-2">Recon</th>
                              <th className="text-right font-semibold pb-2">Amount</th>
                              <th className="text-right font-semibold pb-2">Co-pay</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.filter((p) => p.case_number === fam.case_number && p.family_name === fam.family_name).map((p) => (
                              <tr key={p.id} className="border-t border-stone-200/60">
                                <td className="py-2 text-stone-800">{p.child_name}</td>
                                <td className="py-2 font-mono text-stone-600">{p.payment_id}</td>
                                <td className="py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${p.status === "Paid" ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-700"}`}>{p.status}</span></td>
                                <td className="py-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${p.reconciliation === "Waiting" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{p.reconciliation}</span></td>
                                <td className="py-2 text-right font-medium">${Number(p.amount).toFixed(2)}</td>
                                <td className="py-2 text-right text-stone-600">${Number(p.copay).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  );
                }
                return rows;
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, Icon, tone = "stone", testId }) {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50",
    orange:  "text-orange-700 bg-orange-50",
    amber:   "text-amber-700 bg-amber-50",
    stone:   "text-stone-700 bg-stone-100",
  };
  return (
    <div data-testid={testId} className="rounded-2xl border border-stone-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">{label}</div>
        <span className={`w-7 h-7 rounded-lg grid place-items-center ${tones[tone]}`}><Icon className="w-3.5 h-3.5" /></span>
      </div>
      <div className="mt-2 font-display text-3xl font-medium text-stone-900">{value}</div>
    </div>
  );
}

function LockScreen({ title, description, icon: Icon, children }) {
  return (
    <div className="min-h-[60vh] grid place-items-center p-4" data-testid="billing-lock-screen">
      <div className="max-w-md w-full rounded-3xl border border-stone-200 bg-white shadow-xl p-8">
        <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 grid place-items-center text-emerald-700 mx-auto">
          <Icon className="w-6 h-6" />
        </div>
        <h2 className="mt-4 font-display text-2xl font-medium text-stone-900 text-center">{title}</h2>
        <p className="mt-2 text-sm text-stone-600 text-center">{description}</p>
        <div className="mt-6">{children}</div>
        <div className="mt-5 pt-4 border-t border-stone-100 text-[11px] text-stone-500 text-center">
          Only the center owner can set or verify this passcode.
        </div>
      </div>
    </div>
  );
}

function PasscodeInput({ label, value, onChange, testId, autoFocus = false }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-stone-500">{label}</Label>
      <Input data-testid={testId} type="password" inputMode="text" autoComplete="off" autoFocus={autoFocus}
        value={value} onChange={(e) => onChange(e.target.value)}
        placeholder="••••"
        className="mt-1 h-11 tracking-[0.3em] font-mono" />
    </div>
  );
}

// iter-81ap — Spending Tracker: total income is 100%, each expense shows as
// a percentage of that income. Owners can add expenses under 10 categories
// and see instantly where their money is going.
function SpendingTracker({ spending, onAdd, onDelete }) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("supplies");
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!description.trim()) { toast.error("Add a description"); return; }
    if (Number.isNaN(amt) || amt <= 0) { toast.error("Amount must be a positive number"); return; }
    setSaving(true);
    try {
      await onAdd({ description: description.trim(), amount: amt, category, expense_date: expenseDate });
      setDescription(""); setAmount("");
    } finally { setSaving(false); }
  };

  const gross = spending?.gross_income ?? 0;
  const spent = spending?.total_spent ?? 0;
  const remaining = spending?.remaining ?? 0;
  const spentPct = spending?.spent_pct ?? 0;
  const categories = spending?.categories || {};
  const expenses = spending?.expenses || [];

  // Group categories in descending order of spend
  const categoryRows = Object.entries(categories)
    .map(([k, v]) => ({ key: k, label: CATEGORY_LABEL[k] || k, ...v }))
    .sort((a, b) => b.amount - a.amount);

  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="spending-tracker">
      <div className="p-5 border-b border-stone-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center">
            <PieChart className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-xl text-stone-900">Spending vs. Income</div>
            <div className="text-xs text-stone-500">Every expense shown as a percentage of your total billing income.</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          data-testid="spending-toggle"
        >
          {expanded ? "Collapse" : "Expand"}
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Top tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-stone-100 border-b border-stone-100">
        <SpendTile Icon={Wallet}       label="Income (100%)" value={`$${gross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} tone="emerald" testId="spend-income" />
        <SpendTile Icon={TrendingDown} label="Spent"         value={`$${spent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                   sub={`${spentPct}% of income`} tone={spentPct > 100 ? "red" : spentPct > 75 ? "amber" : "stone"} testId="spend-spent" />
        <SpendTile Icon={DollarSign}   label="Remaining"     value={`$${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                   tone={remaining < 0 ? "red" : "emerald"} testId="spend-remaining" />
        <SpendTile Icon={Receipt}      label="Expense count" value={expenses.length} testId="spend-count" />
      </div>

      {/* Percentage bar */}
      {gross > 0 && (
        <div className="px-5 pt-4">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1.5">
            You&apos;ve spent <span className="text-emerald-800">{spentPct}%</span> of your income
          </div>
          <div className="w-full h-3 rounded-full bg-stone-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${spentPct > 100 ? "bg-red-500" : spentPct > 75 ? "bg-amber-500" : "bg-emerald-500"}`}
              style={{ width: `${Math.min(spentPct, 100)}%`, transition: "width 0.4s" }}
              data-testid="spend-progress-bar"
            />
          </div>
        </div>
      )}

      {/* Add expense form (always visible) */}
      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-12 gap-2 items-end border-b border-stone-100" data-testid="expense-form">
        <div className="md:col-span-4">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g., Art supplies" className="mt-1 h-10" data-testid="expense-desc" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Amount</Label>
          <Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-1 h-10" data-testid="expense-amount" />
        </div>
        <div className="md:col-span-3">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Category</Label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm" data-testid="expense-category">
            {EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Date</Label>
          <Input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className="mt-1 h-10" data-testid="expense-date" />
        </div>
        <div className="md:col-span-1">
          <Button type="submit" disabled={saving} className="w-full h-10 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="expense-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </form>

      {/* Category breakdown + expense list */}
      {expanded && (
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500 mb-2">Category breakdown</div>
            {categoryRows.length === 0 ? (
              <div className="text-sm text-stone-500 italic">No expenses yet.</div>
            ) : (
              <div className="space-y-2" data-testid="category-breakdown">
                {categoryRows.map((c) => (
                  <div key={c.key}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-800">{c.label}</span>
                      <span className="font-mono font-semibold text-stone-900">
                        ${c.amount.toFixed(2)} <span className="text-emerald-700 font-semibold ml-1">({c.percentage}%)</span>
                      </span>
                    </div>
                    <div className="mt-1 w-full h-1.5 rounded-full bg-stone-100 overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(c.percentage, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500 mb-2">All expenses ({expenses.length})</div>
            {expenses.length === 0 ? (
              <div className="text-sm text-stone-500 italic">Add your first expense above.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto pr-1 space-y-1.5" data-testid="expense-list">
                {expenses.map(e => (
                  <div key={e.id} className="flex items-center gap-2 border border-stone-100 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-stone-900 truncate">{e.description}</div>
                      <div className="text-[10px] text-stone-500 flex flex-wrap gap-x-2">
                        <span>{CATEGORY_LABEL[e.category] || e.category}</span>
                        <span>·</span>
                        <span>{e.expense_date}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-semibold text-stone-900">${e.amount.toFixed(2)}</div>
                      <div className="text-[10px] font-semibold text-emerald-700">{e.percentage}%</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onDelete(e.id)}
                      className="text-stone-400 hover:text-red-600"
                      title="Delete"
                      data-testid={`expense-delete-${e.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SpendTile({ Icon, label, value, sub, tone = "stone", testId }) {
  const tones = {
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    red:     "text-red-700",
    stone:   "text-stone-900",
  };
  return (
    <div className="px-5 py-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-stone-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-medium ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// iter-81aq — Billing history filter bar. Shows:
//   • A banner with total weeks imported + earliest/latest dates
//   • Quick chips (All time / This month / Last month / Last 3 months / This year)
//   • Expandable browser to pick a specific month or week for deep history
function BillingHistoryFilters({
  historyMeta, rangeMode, setRangeMode, rangeMonth, setRangeMonth,
  period, setPeriod, deletePeriod, busy,
}) {
  const [browserOpen, setBrowserOpen] = useState(false);
  const months = historyMeta?.months || [];
  const periods = historyMeta?.periods || [];
  const totalWeeks = historyMeta?.total_periods || 0;
  const earliest = historyMeta?.earliest;
  const latest = historyMeta?.latest;

  const QUICK = [
    { key: "all",           label: "All time" },
    { key: "this_month",    label: "This month" },
    { key: "last_month",    label: "Last month" },
    { key: "last_3_months", label: "Last 3 months" },
    { key: "this_year",     label: "This year" },
  ];

  const pickMonth = (m) => {
    setRangeMonth(m);
    setRangeMode("month");
    setBrowserOpen(true);
  };
  const pickWeek = (w) => {
    setPeriod(w);
    setRangeMode("week");
    setBrowserOpen(true);
  };
  const pickAll = () => {
    setRangeMode("all"); setRangeMonth(""); setPeriod("all");
  };

  const monthLabel = (m) => {
    if (!m || m.length < 7) return m || "—";
    const [y, mo] = m.split("-").map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  if (!totalWeeks) {
    return (
      <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 px-5 py-4 text-sm text-stone-600" data-testid="billing-history-empty">
        No billing history yet. Import your first ODJFS payment CSV using the <b>Import CSV</b> button above.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="billing-history-filters">
      {/* Banner */}
      <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 bg-stone-50/70">
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
          <span className="text-stone-800 font-bold">{totalWeeks}</span> week{totalWeeks === 1 ? "" : "s"} imported
          <span className="mx-2 text-stone-300">·</span>
          Earliest <span className="text-stone-700">{earliest || "—"}</span>
          <span className="mx-2 text-stone-300">·</span>
          Latest <span className="text-stone-700">{latest || "—"}</span>
        </div>
        <button
          type="button"
          onClick={() => setBrowserOpen(v => !v)}
          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          data-testid="history-browser-toggle"
        >
          {browserOpen ? "Hide history" : "Browse history"}
          {browserOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Quick chips */}
      <div className="px-5 py-3 flex flex-wrap items-center gap-2">
        {QUICK.map(q => (
          <button
            key={q.key}
            type="button"
            onClick={() => {
              if (q.key === "all") pickAll();
              else { setRangeMode(q.key); setRangeMonth(""); setPeriod("all"); }
            }}
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition ${
              rangeMode === q.key
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white text-stone-700 border-stone-200 hover:border-emerald-300 hover:text-emerald-800"
            }`}
            data-testid={`range-chip-${q.key}`}
          >
            {q.label}
          </button>
        ))}
        {(rangeMode === "month" || rangeMode === "week") && (
          <span
            className="ml-1 px-3 h-8 inline-flex items-center rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
            data-testid="range-chip-custom"
          >
            {rangeMode === "month" ? monthLabel(rangeMonth) : `Week of ${period}`}
            <button
              type="button"
              onClick={pickAll}
              className="ml-2 text-emerald-700 hover:text-emerald-900"
              title="Clear filter"
            >×</button>
          </span>
        )}
        {rangeMode === "week" && period && period !== "all" && (
          <Button
            data-testid="billing-delete-period-btn"
            variant="outline"
            onClick={() => deletePeriod("week")}
            disabled={busy}
            className="ml-auto rounded-xl h-8 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 text-xs px-3"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            Delete this week
          </Button>
        )}
        {rangeMode === "month" && rangeMonth && (
          <Button
            data-testid="billing-delete-month-btn"
            variant="outline"
            onClick={() => deletePeriod("month")}
            disabled={busy}
            className="ml-auto rounded-xl h-8 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 text-xs px-3"
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            Delete this month
          </Button>
        )}
      </div>

      {/* Expandable month/week browser */}
      {browserOpen && (
        <div className="border-t border-stone-100 px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="history-browser">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500 mb-2">
              Months ({months.length})
            </div>
            <div className="max-h-64 overflow-y-auto pr-1 space-y-1" data-testid="history-months">
              {months.map(m => (
                <button
                  key={m.month}
                  type="button"
                  onClick={() => pickMonth(m.month)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition flex items-center justify-between gap-2 ${
                    rangeMode === "month" && rangeMonth === m.month
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-white border-stone-100 hover:border-emerald-200"
                  }`}
                  data-testid={`history-month-${m.month}`}
                >
                  <div>
                    <div className="text-sm font-semibold text-stone-900">{monthLabel(m.month)}</div>
                    <div className="text-[10px] text-stone-500">{m.weeks_count} week{m.weeks_count === 1 ? "" : "s"} · {m.payments} payment{m.payments === 1 ? "" : "s"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono font-semibold text-emerald-800">${m.revenue.toFixed(2)}</div>
                    <div className="text-[10px] text-stone-500">+ ${m.copay.toFixed(2)} copay</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500 mb-2">
              Individual weeks ({periods.length})
            </div>
            <div className="max-h-64 overflow-y-auto pr-1 space-y-1" data-testid="history-weeks">
              {periods.slice().reverse().map(w => (
                <button
                  key={w}
                  type="button"
                  onClick={() => pickWeek(w)}
                  className={`w-full text-left px-3 py-1.5 rounded-lg border transition text-sm font-medium ${
                    rangeMode === "week" && period === w
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-white border-stone-100 text-stone-700 hover:border-emerald-200"
                  }`}
                  data-testid={`history-week-${w}`}
                >
                  Week of {w}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Danger zone — full wipe of every uploaded week/month.
          iter-81c8: keeps a red band well away from the day-to-day filters so
          it can't be tapped accidentally, and requires typing DELETE ALL to
          confirm. */}
      {browserOpen && periods.length > 0 && (
        <div className="border-t border-red-100 bg-red-50/60 px-5 py-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-widest text-red-700">
              Danger zone
            </div>
            <div className="text-xs text-red-800/80 mt-0.5">
              Permanently remove every uploaded billing week and month for this center.
              You can always re-upload CSVs afterwards.
            </div>
          </div>
          <Button
            data-testid="billing-delete-all-btn"
            variant="outline"
            onClick={() => deletePeriod("all")}
            disabled={busy}
            className="rounded-xl h-9 border-red-400 bg-white text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 text-xs px-4 shrink-0"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
            Delete ALL billing history
          </Button>
        </div>
      )}
    </div>
  );
}
